import { Playlists } from '../../../@resources/sounds/music';
import { telemetry } from '../../../@vendor/Posthog';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { ObjectManager } from '../../../concepts/objects';
import { ServerDate } from '../../../platform/ServerDate';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { resolveSubject } from '../../canvas/commands/CommandUtil';
import { EditorSession } from '../../canvas/EditorSession';
import { Physicals } from '../../canvas/entities/Physicals';
import { Jukebox, JukeboxProgressAttr } from './Jukebox';

export const createJukebox = defineCommand(
    'createJukebox',
    'Create: Jukebox...',
    (cc) => {
        Physicals.createAtCameraPosition(cc, Jukebox, x => {
            // x.url = 'https://www.youtube.com/watch?v=7wtfhZwyrcc';
        });
    }
);

export const jukeboxPlayPause = defineCommand(
    'jukeboxPlayPause',
    'Jukebox: Play/Pause',
    (cc, [targetId]: [string?]) => {

        const objects = cc.call(ObjectManager);
        const subject = resolveSubject(cc, targetId);
        const jukebox = subject?.as(Jukebox);

        if (!jukebox) return;

        objects.update(u => {
            u.set('history', 'skip');

            const { currentTrack, elapsed } = jukebox.getCurrentTrack();

            if (jukebox.playing) {
                // If playing should pause
                jukebox.pausedAt = elapsed;
                jukebox.playingSince = -1;
            }
            else {
                jukebox.playingSince = ServerDate.now() - jukebox.pausedAt;
                jukebox.pausedAt = -1;
                telemetry.emit('interaction.jukebox', {
                    track: currentTrack.id,
                });
            }

            jukebox.playingFrom = currentTrack.id;
        });
    }
);

export const jukeboxNext = defineCommand(
    'jukeboxNext',
    'Jukebox: Next',
    (cc, [targetId]: [string?]) => {

        const objects = cc.call(ObjectManager);
        const subject = resolveSubject(cc, targetId);
        const jukebox = subject?.as(Jukebox);

        if (!jukebox) return;

        objects.update(u => {
            u.set('history', 'skip');

            const { currentTrack } = jukebox.getCurrentTrack();
            const tracks = jukebox.getTrackList();
            const currentIndex = tracks.findIndex(x => x.id === currentTrack.id);

            jukebox.playingFrom = tracks[(currentIndex + 1) % tracks.length].id;
            resetJukebox(jukebox);
        });
    }
);

export const jukeboxPrev = defineCommand(
    'jukeboxPrev',
    'Jukebox: Prev',
    (cc, [targetId]: [string?]) => {

        const objects = cc.call(ObjectManager);
        const subject = resolveSubject(cc, targetId);
        const jukebox = subject?.as(Jukebox);

        if (!jukebox) return;

        objects.update(u => {
            u.set('history', 'skip');

            const { currentTrack, elapsed } = jukebox.getCurrentTrack();
            const tracks = jukebox.getTrackList();
            const currentIndex = tracks.findIndex(x => x.id === currentTrack.id);

            if (elapsed < 3000) {
                jukebox.playingFrom = tracks[(currentIndex + tracks.length - 1) % tracks.length].id;
            }

            resetJukebox(jukebox);
        });
    }
);

export const jukeboxShuffle = defineCommand(
    'jukeboxShuffle',
    'Jukebox: Shuffle',
    (cc, [targetId]: [string?]) => {

        const objects = cc.call(ObjectManager);
        const subject = resolveSubject(cc, targetId);
        const jukebox = subject?.as(Jukebox);

        if (!jukebox) return;

        objects.update(u => {
            u.set('history', 'skip');

            updateJukebox(jukebox);

            jukebox.shuffle = !jukebox.shuffle;
            if (jukebox.shuffle) {
                jukebox.seed = (parseInt(jukebox.seed, 10) + 1).toString();
            }
        });
    }
);

export const jukeboxLoop = defineCommand(
    'jukeboxLoop',
    'Jukebox: Loop',
    (cc, [targetId]: [string?]) => {

        const objects = cc.call(ObjectManager);
        const subject = resolveSubject(cc, targetId);
        const jukebox = subject?.as(Jukebox);

        if (!jukebox) return;

        objects.update(u => {
            u.set('history', 'skip');

            updateJukebox(jukebox);

            jukebox.loop = !jukebox.loop;
        });
    }
);

const updateJukebox = (jukebox: Jukebox) => {
    const { currentTrack, elapsed } = jukebox.getCurrentTrack();

    jukebox.playingFrom = currentTrack.id;
    jukebox.playingSince = ServerDate.now() - elapsed;

    const progress = elapsed / currentTrack.length;
    JukeboxProgressAttr.set(jukebox, progress);
};

export const jukeboxShowTracks = defineCommand(
    'jukeboxShowTracks',
    'Jukebox: Show Tracks...',
    (cc, [targetId]: [string?]) => {
        const session = cc.call(EditorSession);

        session.update(AdapterInfo, CreateAdapterInfo(targetId, 'jukebox'));
    }
);

export const jukeboxSetTrack = defineCommand(
    'jukeboxSetTrack',
    'Jukebox: Set Track...',
    (cc, [targetId, playlist, trackIndex]: [string?, string?, number?]) => {

        const objects = cc.call(ObjectManager);
        const subject = resolveSubject(cc, targetId);
        const jukebox = subject?.as(Jukebox);

        if (!jukebox) return;

        objects.update(u => {
            u.set('history', 'skip');

            const { tracks } = Playlists.find(x => x.id === playlist);

            jukebox.playlist = playlist;
            jukebox.playingFrom = tracks[trackIndex].id;
            jukebox.playingSince = ServerDate.now();

            resetJukebox(jukebox);
        });
    }
);

const resetJukebox = (jukebox: Jukebox) => {
    jukebox.pausedAt = -1;
    if (jukebox.playing) {
        jukebox.playingSince = ServerDate.now();
    }
    JukeboxProgressAttr.set(jukebox, 0);
};

export const inJukeboxCommands = () => inCommands(
    createJukebox,
    jukeboxPlayPause,
    jukeboxNext,
    jukeboxPrev,
    jukeboxShuffle,
    jukeboxLoop,
    jukeboxShowTracks,
    jukeboxSetTrack,
);

